import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  InfiniteData,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { NetworkDetector } from "@/services/networkDetector";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";

/**
 * Default options for cached queries that work well with offline functionality
 */
const CACHED_QUERY_DEFAULTS = {
  networkMode: "offlineFirst" as const,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  retry: false,
  staleTime: 5 * 60 * 1000, // 5 minutes - prefer cached data
} as const;

/**
 * Hook that provides useQuery with offline-first defaults.
 *
 * This hook applies sensible defaults for queries that should:
 * - Work with cached data when offline
 * - Not automatically refetch on reconnect/focus
 * - Prefer cached data for better performance
 *
 * Individual options can still be overridden by passing them explicitly.
 *
 * @example
 * ```typescript
 * // Uses cached defaults
 * const query = useCachedQuery({
 *   queryKey: ['clients'],
 *   queryFn: fetchClients,
 * });
 *
 * // Override specific options
 * const query = useCachedQuery({
 *   queryKey: ['clients'],
 *   queryFn: fetchClients,
 *   staleTime: 10 * 60 * 1000, // Override to 10 minutes
 *   refetchOnWindowFocus: true, // Override to allow refetch on focus
 * });
 * ```
 */
export function useCachedQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const query = useQuery({
    ...CACHED_QUERY_DEFAULTS,
    ...options,
    meta: {
      onError: () => {
        toast({
          title: t("errors.genericAPIErrorTitle"),
        });
      },
      ...options.meta,
    },
  });

  // Subscribe to network status changes for regular queries
  useEffect(() => {
    const handleStatusChange = ({ isOnline }: { isOnline: boolean }) => {
      // When online and there was a failed query, retry
      if (isOnline && query.isError && !query.isFetching) {
        // Use setTimeout to ensure the network is stable before retrying
        setTimeout(() => {
          if (!query.isFetching) {
            query.refetch();
          }
        }, 100);
      }
    };

    NetworkDetector.on("online", handleStatusChange);
    return () => NetworkDetector.off("online", handleStatusChange);
  }, [query.isError, query.isFetching, query]);

  return query;
}

/**
 * Hook that provides useInfiniteQuery with offline-first defaults.
 *
 * This hook applies the same offline-first defaults as useCachedQuery but for infinite queries.
 * It's designed for paginated data that should:
 * - Work with cached data when offline
 * - Not automatically refetch on reconnect/focus
 * - Prefer cached data for better performance
 *
 * Individual options can still be overridden by passing them explicitly.
 *
 * @example
 * ```typescript
 * // Uses cached defaults
 * const infiniteQuery = useCachedInfiniteQuery({
 *   queryKey: ['clients'],
 *   queryFn: ({ pageParam }) => fetchClients(pageParam),
 *   initialPageParam: "",
 *   getNextPageParam: (lastPage) => lastPage.nextCursor,
 * });
 *
 * // Override specific options
 * const infiniteQuery = useCachedInfiniteQuery({
 *   queryKey: ['clients'],
 *   queryFn: ({ pageParam }) => fetchClients(pageParam),
 *   initialPageParam: "",
 *   getNextPageParam: (lastPage) => lastPage.nextCursor,
 *   staleTime: 10 * 60 * 1000, // Override to 10 minutes
 *   refetchOnWindowFocus: true, // Override to allow refetch on focus
 * });
 * ```
 */
export function useCachedInfiniteQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryFnData,
    TQueryKey,
    TPageParam
  >,
) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const query = useInfiniteQuery({
    ...CACHED_QUERY_DEFAULTS,
    ...options,
    meta: {
      onError: () => {
        toast({
          title: t("errors.genericAPIErrorTitle"),
        });
      },
      ...options.meta,
    },
  });

  // Subscribe to network status changes
  useEffect(() => {
    const handleStatusChange = ({ isOnline }: { isOnline: boolean }) => {
      // When online and there was a failed page fetch, retry
      if (isOnline && query.isFetchNextPageError && !query.isFetchingNextPage) {
        // Use setTimeout to ensure the network is stable before retrying
        setTimeout(() => {
          if (query.hasNextPage && !query.isFetchingNextPage) {
            query.fetchNextPage();
          }
        }, 100);
      }
    };

    NetworkDetector.on("online", handleStatusChange);
    return () => NetworkDetector.off("online", handleStatusChange);
  }, [
    query.isFetchNextPageError,
    query.hasNextPage,
    query.isFetchingNextPage,
    query,
  ]);

  return query;
}
